import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';
import IfComponent from '../../shared/IfComponent';
import CreateNewPageFromExistingPage from '../CreateNewPageFromExistingPage';
import { CREATE_NEW_PAGE_USING, DEFAULT_COUNTRY_LANGAUGE } from '../../../constants/content-recommendations';
import { useSelector } from 'react-redux';
import { getCountryDetailsByAlpfa3 } from '../../../constants/CountryAndLanguages';
import { useSchema } from '../../../utility/services/customHooks';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../../constants/analyticsMeta';

const CreateContentBrief = () => {
  const [showForm, setShowForm] = useState(false);
  const [showCreateNewPagePopUp, setShowCreateNewPagePopUp] = React.useState(false);
  const [showFormAsLandingPage, setShowFormAsLandingPage] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [defaultCountryLanguage, setDefaultCountryLanguage] = useState<typeof DEFAULT_COUNTRY_LANGAUGE | undefined>(
    undefined
  );
  const [directKeywords, setDirectKeywords] = React.useState<string[] | undefined>(undefined);
  const schemaName = useSchema();
  const customerInfo = useSelector((state) => state.root.customerInfo);
  const [primaryCountry, setPrimaryCountry] = useState({
    ...DEFAULT_COUNTRY_LANGAUGE,
  }); // CUSTOMER COUNTRY

  useEffect(() => {
    if (customerInfo && customerInfo?.length) {
      const customerData = customerInfo.find((customer) => customer?.cust_code === schemaName);
      const countryDetails = getCountryDetailsByAlpfa3(customerData?.primary_country);
      const defaultCountryAndLanguage = {
        ...DEFAULT_COUNTRY_LANGAUGE,
      };
      if (countryDetails) {
        defaultCountryAndLanguage.country = {
          ...defaultCountryAndLanguage.country,
          ...countryDetails,
        };
        defaultCountryAndLanguage.language = {
          ...defaultCountryAndLanguage.language,
          location_code: countryDetails?.location_code,
        };
      }
      setPrimaryCountry({
        ...defaultCountryAndLanguage,
      });
    }
  }, [schemaName, customerInfo]);

  const getCountryAndLanguage = useMemo(() => {
    const pageLavelDetails = { ...DEFAULT_COUNTRY_LANGAUGE };
    if (primaryCountry?.country) {
      pageLavelDetails.country = primaryCountry?.country;
    }
    return {
      country: {
        ...pageLavelDetails.country,
      },
      language: {
        ...pageLavelDetails.language,
        location_code: pageLavelDetails.country.location_code,
      },
    };
  }, [primaryCountry]);

  const handleMenuItemClick = (index: number) => {
    setMenuOpen(false);
    if (index === 0) {
      // Create New Page
      trackAnalyticsEvent({
        eventFor: '',
        buttonText: '',
        eventName: 'Create New Page Form Open',
        payload: {
          label: 'Create New Page Form Open',
          component_name: COMPONENT_NAMES.content_recommendations,
          screen_name: SCREEN_NAMES.content_recommendations,
        },
      });
      setShowForm(true);
    } else if (index === 1) {
      // Create Landing Page
      trackAnalyticsEvent({
        eventFor: '',
        buttonText: '',
        eventName: 'Create Landing Page Wizard Open',
        payload: {
          label: 'Create Landing Page Wizard Open',
          component_name: COMPONENT_NAMES.content_recommendations,
          screen_name: SCREEN_NAMES.content_recommendations,
        },
      });
      setShowFormAsLandingPage(true);
    }
  };

  return (
    <>
      <span>
        <ButtonGroup
          variant="contained"
          ref={anchorRef}
          aria-label="create page options"
          className="q-section-header-btn-group"
        >
          <Button
            className="q-section-header-btn"
            data-testid="add-new-link-button"
            onClick={() => {
              trackAnalyticsEvent({
                eventFor: '',
                buttonText: '',
                eventName: 'Create New Page Form Open',
                payload: {
                  label: 'Create New Page Form Open',
                  component_name: COMPONENT_NAMES.content_recommendations,
                  screen_name: SCREEN_NAMES.content_recommendations,
                },
              });
              setShowForm(true);
            }}
            startIcon={<AddIcon />}
          >
            Create New Page
          </Button>
          <Button
            className="q-section-header-btn"
            size="small"
            aria-controls={menuOpen ? 'create-page-menu' : undefined}
            aria-expanded={menuOpen ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((prev) => !prev)}
            data-testid="create-page-dropdown-button"
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper open={menuOpen} anchorEl={anchorRef.current} role={undefined} transition style={{ zIndex: 1301 }}>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
            >
              <Paper className="create-page-dropdown-paper">
                <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                  <MenuList id="create-page-menu" autoFocusItem>
                    <MenuItem className="create-page-menu-item" onClick={() => handleMenuItemClick(0)}>
                      Create New Page
                    </MenuItem>
                    <Divider />
                    <MenuItem className="create-page-menu-item" onClick={() => handleMenuItemClick(1)}>
                      Create Landing Page
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </span>

      {/* Existing Create New Page dialog */}
      <IfComponent isValid={showForm}>
        <CreateNewPageFromExistingPage
          creatingPageFrom={CREATE_NEW_PAGE_USING?.FROM_NEW}
          setShowCreateNewPagePopUp={setShowForm}
          language_country_details={defaultCountryLanguage || getCountryAndLanguage}
          redirectToNewTab={true}
        />
      </IfComponent>
      {showCreateNewPagePopUp ? (
        <CreateNewPageFromExistingPage
          creatingPageFrom={CREATE_NEW_PAGE_USING?.FROM_KEYWORD_GAP}
          setShowCreateNewPagePopUp={setShowCreateNewPagePopUp}
          language_country_details={defaultCountryLanguage || getCountryAndLanguage}
          discoveredKeywords={directKeywords}
          redirectToNewTab={true}
        />
      ) : null}

      {/* Landing Page creation — same form with page type pre-selected */}
      <IfComponent isValid={showFormAsLandingPage}>
        <CreateNewPageFromExistingPage
          creatingPageFrom={CREATE_NEW_PAGE_USING?.FROM_NEW}
          setShowCreateNewPagePopUp={setShowFormAsLandingPage}
          language_country_details={defaultCountryLanguage || getCountryAndLanguage}
          redirectToNewTab={true}
          initialPageType="landing_page"
        />
      </IfComponent>
    </>
  );
};

export default React.memo(CreateContentBrief);
